import styles from './Avatar.module.css'

type Size = 14 | 18 | 20 | 24

interface AvatarProps {
  name: string
  /** CSS variable name from tokens.css, e.g. "--user-1". Assigned by users.id (ui-spec §6). */
  colorVar?: string
  size?: Size
  ring?: boolean
  title?: string
}

const FONT_SIZE: Record<Size, number> = { 14: 8, 18: 9.5, 20: 10, 24: 11 }

export function Avatar({
  name,
  colorVar = '--user-1',
  size = 24,
  ring = false,
  title,
}: AvatarProps): React.JSX.Element {
  const initial = name.trim().charAt(0).toUpperCase() || '?'

  return (
    <span
      className={ring ? `${styles.avatar} ${styles.ring}` : styles.avatar}
      style={{
        // Runtime value from data, not from the design — the exception ADR 0009 allows.
        ['--user' as string]: `var(${colorVar})`,
        width: size,
        height: size,
        fontSize: FONT_SIZE[size],
      }}
      title={title ?? name}
      // role="img" is what makes aria-label count. On a bare <span> the label is ignored by the
      // accessible name computation, so a screen reader announced the letter "M" with no context
      // — the attribute was there, the behaviour was not.
      role="img"
      aria-label={title ?? name}
    >
      {/* Hidden from assistive tech: the label above already says who this is, and without this
          the initial is read a second time as loose text. */}
      <span aria-hidden="true">{initial}</span>
    </span>
  )
}

/** Stable colour per user, so avatar, Yjs caret and the "typing" dot always agree (ui-spec §6). */
export function userColorVar(userId: number): string {
  return `--user-${((userId - 1) % 4) + 1}`
}
